'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { apiGetMe } from '../helpers/auth';

type BanInfo = {
  reason: string;
  expires_at: string | null;
  permanent: boolean;
  appealable: boolean;
  banned_at: string | null;
};

// Pages that must remain reachable even when the user is banned, so they can
// read the ban notice, appeal, or log out.
const ALLOWED_PATHS = new Set<string>([
  '/suspended',
  '/login',
  '/register',
  '/reset-password',
  '/check-email',
  '/contact-us',
]);

function isAllowed(pathname: string) {
  if (!pathname) return false;
  if (ALLOWED_PATHS.has(pathname)) return true;
  // Allow the legal pages
  if (pathname.startsWith('/(legal)')) return true;
  return false;
}

export default function BanGuard({ children }: { children: ReactNode }) {
  const pathname = usePathname() || '/';
  const router = useRouter();
  const [checked, setChecked] = useState(false);
  const [ban, setBan] = useState<BanInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiGetMe()
      .then((data: any) => {
        if (cancelled) return;
        const b = data?.user?.ban ?? null;
        setBan(b);
      })
      .catch(() => {
        if (!cancelled) setBan(null);
      })
      .finally(() => {
        if (!cancelled) setChecked(true);
      });
    return () => { cancelled = true; };
  }, [pathname]);

  useEffect(() => {
    if (!checked) return;
    if (ban && !isAllowed(pathname)) {
      router.replace('/suspended');
    }
  }, [ban, checked, pathname, router]);

  // If banned and not yet on an allowed page, block rendering of children to
  // avoid flashing restricted UI before the redirect completes.
  if (checked && ban && !isAllowed(pathname)) {
    return null;
  }

  return <>{children}</>;
}
